import { listAllMedia } from "./media.js";
import {
	listTvSeries,
	getCatalog as getTvCatalog,
	effectiveSeen as tvSeen,
	effectiveRating as tvRating,
} from "./tv.js";
import {
	listAnime,
	getCatalog as getAnimeCatalog,
	effectiveSeen as animeSeen,
	effectiveRating as animeRating,
} from "./anime.js";
import { PLAYED_STATUSES } from "../components/status-badge.js";
import { todayDate } from "../utils/dates.js";
import { seasonReleased, seasonPremiereDate } from "../utils/seasons.js";

// v1 "Recommendations" — built from signals you've already given, no scoring
// model yet. Two outputs:
//   • recommendations  — what to engage with NEXT (the "want" pool + already-
//     released seasons you haven't watched of series you watched & enjoyed).
//   • upcomingSeasons  — not-yet-released seasons of those same series, shaped
//     like normalized media so they fold straight into the Incoming / Release
//     schedule list via utils/release.js `upcoming()`.

// Engagement states that mean "you've already engaged with it" — such items are
// not recommendation candidates even if their ownership is still `want_it`.
const PLAYED = new Set([...PLAYED_STATUSES, "done"]);
// Series/season rating (1–5) at or above which we'll suggest more of a series.
const ENJOYED_MIN = 3;

// On-my-radar / Want-it items you haven't started yet, across every media type.
function wantPool(allMedia) {
	return allMedia
		.filter((m) => {
			const eng = m.engagement_status;
			if (eng && (PLAYED.has(eng) || eng === "not_interested")) return false;
			return eng === "want_to_check" || m.ownership_status === "want_it";
		})
		.map((m) => ({
			...m,
			reason: m.engagement_status === "want_to_check" ? "radar" : "want_it",
		}));
}

// Pure core (exported for tests): given a series, its cached catalog and the
// current year, split its seasons into already-released "next up" picks and
// future "upcoming" seasons — but only when the series is one you've watched and
// rated highly enough. `seenFn`/`ratingFn` are the media-type resolvers
// (effectiveSeen / effectiveRating from db/tv.js or db/anime.js). Returns
// `{ next: season[], upcoming: season[] }`; empty when the series doesn't qualify
// or has no catalog.
export function classifySeasons(series, catalog, today, seenFn, ratingFn) {
	const out = { next: [], upcoming: [] };
	if (!catalog) return out;
	const seasons = (catalog.seasons ?? []).filter((s) => s.number != null);
	const episodes = catalog.episodes ?? [];

	// "Watched and enjoyed": you've seen at least one RELEASED season (or the whole
	// series is Watched), and the best rating you've given it is ≥ ENJOYED_MIN. An
	// unaired season can't count as seen/rated, so it's excluded here.
	let hasSeen = series.engagement_status === "done";
	let bestRating = series.rating ?? 0;
	for (const s of seasons) {
		const released = seasonReleased(s, episodes, today);
		if (released && seenFn(series, s.number, null, { released })) {
			hasSeen = true;
			bestRating = Math.max(
				bestRating,
				ratingFn(series, s.number, null, { released }) ?? 0,
			);
		}
	}
	if (!hasSeen || bestRating < ENJOYED_MIN) return out;

	for (const s of seasons) {
		const released = seasonReleased(s, episodes, today);
		if (!released) {
			// Not aired yet → an upcoming season, regardless of any inherited "seen".
			out.upcoming.push(s);
		} else if (!seenFn(series, s.number, null, { released })) {
			out.next.push(s);
		}
	}
	return out;
}

// Shape a season as a normalized-media-like item so both the Recommendations
// view and the Release schedule (`upcoming()` + monthBucket) render it unchanged.
// `episodes` lets us attach the precise premiere date so an upcoming season sorts
// and notifies on the right day (not just its year).
export function seasonItem(series, mediaType, s, reason, episodes) {
	const base = mediaType === "anime" ? "anime" : "tv";
	const premiere = seasonPremiereDate(s, episodes);
	// Only keep a precise date when it carries a day (year-fallback dates like
	// "2027-01-01" stay as release_year so they bucket as "the year", not Jan 1).
	const preciseDate = s.premiere_date || (premiere && !premiere.endsWith("-01-01") ? premiere : null);
	return {
		id: `${series.id}:s${s.number}`,
		media_type: mediaType,
		title: series.title,
		secondary: s.name || `Season ${s.number}`,
		cover_url: s.cover_url ?? series.cover_url ?? null,
		release_year: s.premiere_year ?? null,
		release_date: preciseDate,
		route: `/${base}/${series.id}/season/${s.number}`,
		reason,
	};
}

async function seasonSuggestions(list, mediaType, getCat, seenFn, ratingFn, today) {
	const recommendations = [];
	const upcomingSeasons = [];
	for (const series of list) {
		if (!series.tvmaze_id) continue;
		// Cached only — no network on home load (series never opened just don't
		// contribute season suggestions yet).
		const catalog = await getCat(series.tvmaze_id);
		const episodes = catalog?.episodes ?? [];
		const { next, upcoming } = classifySeasons(
			series,
			catalog,
			today,
			seenFn,
			ratingFn,
		);
		for (const s of next) {
			recommendations.push(
				seasonItem(series, mediaType, s, "next_season", episodes),
			);
		}
		for (const s of upcoming) {
			upcomingSeasons.push(
				seasonItem(series, mediaType, s, "upcoming_season", episodes),
			);
		}
	}
	return { recommendations, upcomingSeasons };
}

// Build the full suggestion set. `now` is injectable for tests.
export async function buildSuggestions(today = todayDate()) {
	const [allMedia, tv, anime] = await Promise.all([
		listAllMedia(),
		listTvSeries(),
		listAnime(),
	]);

	const [tvSug, animeSug] = await Promise.all([
		seasonSuggestions(tv, "tv", getTvCatalog, tvSeen, tvRating, today),
		seasonSuggestions(anime, "anime", getAnimeCatalog, animeSeen, animeRating, today),
	]);

	const byTitle = (a, b) => (a.title ?? "").localeCompare(b.title ?? "");
	// Season picks first (highest-signal: more of something you loved), then the
	// want pool; alphabetical within each group.
	const seasonRecs = [...tvSug.recommendations, ...animeSug.recommendations].sort(
		byTitle,
	);
	const wants = wantPool(allMedia).sort(byTitle);

	return {
		recommendations: [...seasonRecs, ...wants],
		upcomingSeasons: [...tvSug.upcomingSeasons, ...animeSug.upcomingSeasons],
	};
}
